import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { CrudStyles } from '../shared/Crud';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import { useCrud } from '../../hooks/useCrud';
import invoiceService from '../../services/invoiceService';
import { formatCurrencyBR, formatDateBR } from '../../utils/masks';
import { formatDateTimeBR } from '../../utils/dateTimeDisplay';
import {
    MAX_DELIVERY_DATE_RANGE_DAYS,
    getDefaultDeliveryDateRange,
    validateDeliveryDateRange,
} from '../../utils/deliveryDateRange';
import { getSelectableInvoiceStatusOptions } from './invoiceStatusConfig';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import DeliveryStatusMultiSelect from '../Deliveries/DeliveryStatusMultiSelect';
import { StatusFilterGroup, ActionCell, ActionBtn } from '../Deliveries/DeliveriesList.styles';
import { PlusIcon, SearchIcon, EyeIcon } from '../Layout/icons';

const {
    Container,
    Header,
    Button,
    FiltersContainer,
    FilterGroup,
    TableContainer,
    PaginationContainer,
    PageInfo,
    PageButtons,
    Table,
} = CrudStyles;

const InvoiceIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);

function formatCreationDate(value) {
    if (!value) return '—';
    const formatted = formatDateTimeBR(value);
    return formatted || '—';
}

const defaultDateRange = getDefaultDeliveryDateRange();

export default function FaturasList() {
    const navigate = useNavigate();
    const { selectedCustomerId, isManagingOwnData, isTransportCompany } = useCustomerSelector();
    const prevCustomerRef = useRef(selectedCustomerId);
    const prevManagingRef = useRef(isManagingOwnData);

    const showCustomerColumn = isTransportCompany && isManagingOwnData;
    const [filterError, setFilterError] = useState(null);

    const adaptedService = useMemo(() => {
        const wrapList = (params, customerId) => {
            const validation = validateDeliveryDateRange(params.startDate, params.endDate);
            if (!validation.valid) {
                return Promise.resolve({
                    content: [],
                    page: { totalPages: 0, number: 0, first: true, last: true },
                });
            }
            return invoiceService.list({ ...params, customerId });
        };

        if (!isTransportCompany) {
            if (!selectedCustomerId) {
                return {
                    list: async () => ({
                        content: [],
                        page: { totalPages: 0, number: 0, first: true, last: true },
                    }),
                };
            }
            return {
                list: (params) => wrapList(params, selectedCustomerId),
            };
        }

        if (isManagingOwnData || !selectedCustomerId) {
            return {
                list: (params) => wrapList(params, undefined),
            };
        }

        return {
            list: (params) => wrapList(params, selectedCustomerId),
        };
    }, [isTransportCompany, selectedCustomerId, isManagingOwnData]);

    const {
        data,
        loading,
        pagination,
        handlePageChange,
        sorting,
        handleSort,
        localFilters,
        handleFilterChange,
        handleSearch,
        setLocalFilters,
        setRefetchTrigger,
    } = useCrud(adaptedService, {
        pageSize: 20,
        defaultSort: 'invoiceId,desc',
        defaultFilters: {
            invoiceId: '',
            state: [],
            ...defaultDateRange,
        },
    });

    useEffect(() => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        setFilterError(validation.valid ? null : validation.message);
    }, [localFilters.startDate, localFilters.endDate]);

    useEffect(() => {
        const customerChanged = prevCustomerRef.current !== selectedCustomerId;
        const managingChanged = prevManagingRef.current !== isManagingOwnData;
        prevCustomerRef.current = selectedCustomerId;
        prevManagingRef.current = isManagingOwnData;
        if (customerChanged || managingChanged) {
            setRefetchTrigger((prev) => prev + 1);
        }
    }, [selectedCustomerId, isManagingOwnData, setRefetchTrigger]);

    const getSortIcon = (field) => {
        if (!sorting || sorting.field !== field) return null;
        return sorting.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const onSortClick = (field) => {
        if (!handleSort) return;
        const isAsc = sorting.field === field && sorting.direction === 'asc';
        handleSort(field, isAsc ? 'desc' : 'asc');
    };

    const handleStatusFilterChange = (selectedStatuses) => {
        setLocalFilters((prev) => ({ ...prev, state: selectedStatuses }));
    };

    const handleInvoiceSearch = () => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
            return;
        }
        setFilterError(null);
        handleSearch();
    };

    const handleNewInvoice = () => {
        if (isTransportCompany && isManagingOwnData) {
            navigate(ROUTES.FATURAS_NOVA);
            return;
        }
        navigate(ROUTES.FATURAS_NOVA);
    };

    const handleView = (e, item) => {
        e.stopPropagation();
        navigate(ROUTES.FATURAS_DETALHE(item.invoiceId));
    };

    const canCreateInvoice = !isTransportCompany || !isManagingOwnData;

    return (
        <PageWithCustomerSelector
            selectorFirstOptionLabel="Todas as empresas"
            selectorFirstOptionDescription="Gerenciar todas as faturas"
            includePhysicalCustomers
        >
            <Container>
                <Header>
                    <h1>Faturas</h1>
                    {canCreateInvoice && (
                        <Button type="button" variant="primary" onClick={handleNewInvoice}>
                            <PlusIcon />
                            Nova fatura
                        </Button>
                    )}
                </Header>

                <FiltersContainer>
                    <FilterGroup>
                        <label htmlFor="invoice-filter-id">Fatura</label>
                        <input
                            id="invoice-filter-id"
                            type="number"
                            name="invoiceId"
                            min="1"
                            placeholder="Nº da fatura"
                            value={localFilters.invoiceId}
                            onChange={handleFilterChange}
                        />
                    </FilterGroup>
                    <StatusFilterGroup>
                        <DeliveryStatusMultiSelect
                            id="invoice-filter-state"
                            label="Estado"
                            value={Array.isArray(localFilters.state) ? localFilters.state : []}
                            options={getSelectableInvoiceStatusOptions()}
                            onChange={handleStatusFilterChange}
                        />
                    </StatusFilterGroup>
                    <FilterGroup>
                        <label htmlFor="invoice-filter-start">Criação de</label>
                        <input
                            id="invoice-filter-start"
                            type="date"
                            name="startDate"
                            value={localFilters.startDate}
                            onChange={handleFilterChange}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <label htmlFor="invoice-filter-end">Criação até</label>
                        <input
                            id="invoice-filter-end"
                            type="date"
                            name="endDate"
                            value={localFilters.endDate}
                            onChange={handleFilterChange}
                        />
                    </FilterGroup>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleInvoiceSearch}
                        style={{ height: '38px' }}
                    >
                        <SearchIcon />
                        Pesquisar
                    </Button>
                </FiltersContainer>
                {filterError ? (
                    <p style={{ margin: '-8px 0 0', color: '#b91c1c', fontSize: 13 }}>{filterError}</p>
                ) : (
                    <p style={{ margin: '-8px 0 0', color: '#6b7280', fontSize: 12 }}>
                        Filtro por data de criação da fatura (intervalo máximo de {MAX_DELIVERY_DATE_RANGE_DAYS} dias).
                    </p>
                )}

                <TableContainer>
                    <Table style={{ minWidth: '640px' }}>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => onSortClick('invoiceId')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Fatura{getSortIcon('invoiceId')}
                                </th>
                                {showCustomerColumn && <th>Cliente</th>}
                                <th
                                    onClick={() => onSortClick('amount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Valor{getSortIcon('amount')}
                                </th>
                                <th
                                    onClick={() => onSortClick('state')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Estado{getSortIcon('state')}
                                </th>
                                <th
                                    onClick={() => onSortClick('dayToCharge')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Data de cobrança{getSortIcon('dayToCharge')}
                                </th>
                                <th
                                    onClick={() => onSortClick('creationDateTime')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Criação{getSortIcon('creationDateTime')}
                                </th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={showCustomerColumn ? 7 : 6}
                                        style={{ textAlign: 'center', padding: '32px' }}
                                    >
                                        Carregando...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={showCustomerColumn ? 7 : 6}
                                        style={{ textAlign: 'center', padding: '32px' }}
                                    >
                                        <InvoiceIcon />
                                        <div style={{ marginTop: 8 }}>Nenhuma fatura encontrada</div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.invoiceId}>
                                        <td>#{item.invoiceId}</td>
                                        {showCustomerColumn && <td>{item.customerName || '—'}</td>}
                                        <td>{formatCurrencyBR(item.amount) || 'R$ 0,00'}</td>
                                        <td>
                                            <InvoiceStatusBadge state={item.state} />
                                        </td>
                                        <td>{formatDateBR(item.dayToCharge) || '—'}</td>
                                        <td>{formatCreationDate(item.creationDateTime)}</td>
                                        <ActionCell>
                                            <ActionBtn
                                                type="button"
                                                onClick={(e) => handleView(e, item)}
                                                title="Detalhes"
                                            >
                                                <EyeIcon />
                                                Detalhes
                                            </ActionBtn>
                                        </ActionCell>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </TableContainer>

                {pagination && (
                    <PaginationContainer>
                        <PageInfo>
                            Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de{' '}
                            {pagination.totalPages || 0}
                        </PageInfo>
                        <PageButtons>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={pagination.first || pagination.totalPages === 0}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={pagination.last || pagination.totalPages === 0}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                Próxima
                            </Button>
                        </PageButtons>
                    </PaginationContainer>
                )}
            </Container>
        </PageWithCustomerSelector>
    );
}
